import { useEffect, useState } from 'react';

import { getJWTToken, parseJwt } from '../../utils/auth';
import type { ScoreUnit } from './types';
import { currentSchoolYear } from './utils/schoolYear';
import { authHeaders } from '../../core/impersonation';

export type GenerationPhase = 'idle' | 'submitting' | 'generating' | 'ready' | 'failed' | 'error';
export type PeInputsProvider = () => Promise<unknown>;

type GenerationOpts = {
  inputsProvider: PeInputsProvider;
  unitMode?: ScoreUnit;
  onReady?: () => void;
  enabled?: boolean;
  schoolYear?: string;
};

type GenerationResult = {
  phase: GenerationPhase;
  jobId: string | null;
  jobUpdatedAt: string | null;
  errorClass: string | null;
  pollExhausted: boolean;
  freshJob: boolean;
  submit: () => void;
  retry: () => void;
  regenerate: () => void;
};

type JobResponse = {
  jobId: string;
  status: 'queued' | 'running' | 'persisting' | 'succeeded' | 'failed' | 'cancelled';
  schoolYear?: string;
  artifactVersion?: number;
  errorClass?: string;
  updatedAt?: string;
  stalled?: boolean;
};

type StoredGeneration = {
  idempotencyKey: string;
  jobId?: string;
};

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';
const POLL_INTERVAL_MS = 4000;
const MAX_CONSECUTIVE_POLL_FAILURES = 5;
export const QUEUE_EXPIRED_ERROR = 'worker_not_started';
export const RETRY_WINDOW_CLOSED_ERROR = 'retry_window_closed';
export const STALLED_ERROR = 'generation_stalled';

function stalledError(job: JobResponse): string | null {
  if (job.stalled !== true) return null;
  if (job.status === 'queued') return QUEUE_EXPIRED_ERROR;
  return job.status === 'running' || job.status === 'persisting' ? STALLED_ERROR : null;
}

const storageKey = (sub: string, schoolYear: string) => `pe-generation:${sub}:${schoolYear}`;

function subFromToken(token: string): string {
  try {
    const sub = parseJwt(token).sub;
    return typeof sub === 'string' && sub !== '' ? sub : 'unknown';
  } catch {
    return 'unknown';
  }
}

function readStored(sub: string, schoolYear: string): StoredGeneration | null {
  try {
    const raw = sessionStorage.getItem(storageKey(sub, schoolYear));
    return raw ? (JSON.parse(raw) as StoredGeneration) : null;
  } catch {
    return null;
  }
}

function writeStored(sub: string, schoolYear: string, value: StoredGeneration): void {
  try {
    sessionStorage.setItem(storageKey(sub, schoolYear), JSON.stringify(value));
  } catch {
    return;
  }
}

function clearStored(sub: string, schoolYear: string): void {
  try {
    sessionStorage.removeItem(storageKey(sub, schoolYear));
  } catch {
    return;
  }
}

type ControllerDeps = {
  setPhaseState: (phase: GenerationPhase) => void;
  setJobIdState: (jobId: string | null) => void;
  setJobUpdatedAtState: (updatedAt: string | null) => void;
  setPollExhaustedState: (exhausted: boolean) => void;
  setErrorClassState: (errorClass: string | null) => void;
  setFreshJobState: (fresh: boolean) => void;
};

type Controller = {
  submit: () => void;
  retry: () => void;
  regenerate: () => void;
  attach: () => void;
  detach: () => void;
  setOpts: (next: GenerationOpts) => void;
};

function errorField(body: unknown): string | null {
  const error = (body as { error?: unknown } | null)?.error;
  return typeof error === 'string' && error !== '' ? error : null;
}

async function rejectionClass(response: Response): Promise<string | null> {
  return errorField(await response.json().catch(() => null));
}

function cancelledStatus(body: unknown): boolean {
  return typeof body === 'object' && body !== null && (body as { status?: unknown }).status === 'cancelled';
}

function createController(deps: ControllerDeps, initialOpts: GenerationOpts): Controller {
  // Read at each use rather than captured: the year arrives with the server's
  // data status, which can resolve after the first render.
  let adoptedYear: string | null = null;
  const schoolYear = () => adoptedYear ?? opts.schoolYear ?? currentSchoolYear(new Date());
  let opts = initialOpts;
  let phase: GenerationPhase = 'idle';
  let jobId: string | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let consecutiveFailures = 0;
  let readyFired = false;
  let active = true;
  let memoryKey: string | null = null;
  let forceFresh = false;
  let resumePollOnError = false;
  let errorClass: string | null = null;

  const setPhase = (next: GenerationPhase) => {
    phase = next;
    deps.setPhaseState(next);
  };

  const setJob = (id: string) => {
    jobId = id;
    deps.setJobIdState(id);
  };

  const stopTimer = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  let storageSub = 'unknown';

  // Takes the namespace the caller is finishing with: recomputing it here
  // could clear a different year than the one this run wrote.
  const becomeReady = (namespaceYear = schoolYear()) => {
    clearStored(storageSub, namespaceYear);
    setPhase('ready');
    if (readyFired) return;
    readyFired = true;
    opts.onReady?.();
  };

  const schedulePoll = (id: string) => {
    stopTimer();
    timer = setTimeout(() => {
      void pollOnce(id);
    }, POLL_INTERVAL_MS);
  };

  const becomeIdleStale = () => {
    clearStored(storageSub, schoolYear());
    memoryKey = null;
    jobId = null;
    deps.setJobIdState(null);
    deps.setJobUpdatedAtState(null);
    deps.setErrorClassState(null);
    setPhase('idle');
  };

  const pollOnce = async (id: string) => {
    try {
      const token = await getJWTToken();
      const response = await fetch(`${apiUrl}/program-evaluation/jobs/${id}`, {
        headers: authHeaders(token),
      });
      if (response.status === 404) {
        if (active) becomeIdleStale();
        return;
      }
      if (!response.ok) {
        throw new Error(`program-evaluation job fetch failed: ${response.status}`);
      }
      const job = (await response.json()) as JobResponse;
      if (!active) return;
      consecutiveFailures = 0;
      if (job.status === 'succeeded') {
        becomeReady();
        return;
      }
      if (job.status === 'failed') {
        deps.setErrorClassState(job.errorClass ?? null);
        deps.setJobUpdatedAtState(job.updatedAt ?? null);
        setPhase('failed');
        return;
      }
      if (job.status === 'cancelled') {
        becomeIdleStale();
        return;
      }
      const stalled = stalledError(job);
      if (stalled !== null) {
        resumePollOnError = false;
        errorClass = stalled;
        deps.setErrorClassState(stalled);
        deps.setJobUpdatedAtState(job.updatedAt ?? null);
        setPhase('error');
        return;
      }
      schedulePoll(id);
    } catch {
      if (!active) return;
      consecutiveFailures += 1;
      if (consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
        resumePollOnError = true;
        deps.setPollExhaustedState(true);
        setPhase('error');
        return;
      }
      schedulePoll(id);
    }
  };

  const startGenerating = (id: string) => {
    setJob(id);
    deps.setJobUpdatedAtState(null);
    deps.setPollExhaustedState(false);
    setPhase('generating');
    consecutiveFailures = 0;
    resumePollOnError = false;
    schedulePoll(id);
  };

  const submit = async () => {
    setPhase('submitting');
    deps.setErrorClassState(null);
    // Pinned for this submit: reading it again mid-flight could write the key
    // and the accepted job under different years.
    const submitYear = schoolYear();
    try {
      const token = await getJWTToken();
      storageSub = subFromToken(token);
      const stored = readStored(storageSub, submitYear);
      let idempotencyKey = forceFresh ? null : (stored?.idempotencyKey ?? memoryKey);
      const createdKey = !idempotencyKey;
      if (!idempotencyKey) {
        idempotencyKey = crypto.randomUUID();
        writeStored(storageSub, submitYear, { idempotencyKey });
        forceFresh = false;
      }
      memoryKey = idempotencyKey;
      const bodyFragment = (await opts.inputsProvider()) as Record<string, unknown>;
      if (!active) return;
      const response = await fetch(`${apiUrl}/program-evaluation`, {
        method: 'POST',
        headers: {
          ...authHeaders(token),
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify({
          unitMode: opts.unitMode ?? 'proficiency_level',
          ...bodyFragment,
        }),
      });
      if (!active) return;
      if (response.status === 202) {
        const job = (await response.json()) as JobResponse;
        deps.setFreshJobState(createdKey);
        // The server assembled from its own uploads, so its year is the one a
        // later mount will look under; the pinned key would be orphaned.
        adoptedYear = job.schoolYear ?? submitYear;
        if (adoptedYear !== submitYear) clearStored(storageSub, submitYear);
        writeStored(storageSub, adoptedYear, { idempotencyKey, jobId: job.jobId });
        if (job.status === 'succeeded') {
          setJob(job.jobId);
          becomeReady();
          return;
        }
        if (job.status === 'failed') {
          setJob(job.jobId);
          deps.setErrorClassState(job.errorClass ?? null);
          deps.setJobUpdatedAtState(job.updatedAt ?? null);
          setPhase('failed');
          return;
        }
        startGenerating(job.jobId);
        return;
      }
      if (response.status === 200) {
        deps.setFreshJobState(createdKey);
        becomeReady(submitYear);
        return;
      }
      if (response.status === 409) {
        const conflict = (await response.json().catch(() => null)) as {
          jobId?: string;
          schoolYear?: string;
          error?: string;
        } | null;
        if (!conflict?.jobId) {
          errorClass = errorField(conflict);
          deps.setErrorClassState(errorClass);
          setPhase('error');
          return;
        }
        deps.setFreshJobState(false);
        // The active job may be generating for a newer year than this client
        // last saw, and that is the key a later mount will look under.
        adoptedYear = conflict.schoolYear ?? submitYear;
        if (adoptedYear !== submitYear) clearStored(storageSub, submitYear);
        writeStored(storageSub, adoptedYear, { idempotencyKey, jobId: conflict.jobId });
        startGenerating(conflict.jobId);
        return;
      }
      deps.setErrorClassState(await rejectionClass(response));
      setPhase('error');
    } catch {
      if (!active) return;
      deps.setErrorClassState(null);
      setPhase('error');
    }
  };

  const retry = async () => {
    if (phase !== 'failed' && phase !== 'error') return;
    if (phase === 'error' && (errorClass === QUEUE_EXPIRED_ERROR || errorClass === RETRY_WINDOW_CLOSED_ERROR)) {
      await regenerate();
      return;
    }
    if (jobId === null) {
      await submit();
      return;
    }
    if (phase === 'error' && resumePollOnError) {
      deps.setErrorClassState(null);
      startGenerating(jobId);
      return;
    }
    const id = jobId;
    setPhase('submitting');
    try {
      const token = await getJWTToken();
      const response = await fetch(`${apiUrl}/program-evaluation/jobs/${id}/retry`, {
        method: 'POST',
        headers: authHeaders(token),
      });
      if (!active) return;
      if (response.status === 202) {
        errorClass = null;
        deps.setErrorClassState(null);
        startGenerating(id);
        return;
      }
      resumePollOnError = false;
      const body: unknown = await response.json().catch(() => null);
      if (cancelledStatus(body)) {
        becomeIdleStale();
        return;
      }
      errorClass = errorField(body);
      deps.setErrorClassState(errorClass);
      setPhase('error');
    } catch {
      if (!active) return;
      resumePollOnError = false;
      deps.setErrorClassState(null);
      setPhase('error');
    }
  };

  const regenerate = async () => {
    if (phase === 'submitting' || phase === 'generating') return;
    memoryKey = null;
    jobId = null;
    errorClass = null;
    deps.setJobIdState(null);
    deps.setJobUpdatedAtState(null);
    deps.setPollExhaustedState(false);
    deps.setErrorClassState(null);
    deps.setFreshJobState(false);
    readyFired = false;
    forceFresh = true;
    clearStored(storageSub, schoolYear());
    setPhase('submitting');
    try {
      const token = await getJWTToken();
      storageSub = subFromToken(token);
    } catch {
      setPhase('error');
      return;
    }
    if (!active) return;
    clearStored(storageSub, schoolYear());
    await submit();
  };

  return {
    submit: () => {
      void submit();
    },
    retry: () => {
      void retry();
    },
    regenerate: () => {
      void regenerate();
    },
    attach: () => {
      active = true;
      if (opts.enabled === false || phase !== 'idle') return;
      void (async () => {
        try {
          const token = await getJWTToken();
          storageSub = subFromToken(token);
        } catch {
          return;
        }
        if (!active || phase !== 'idle') return;
        const stored = readStored(storageSub, schoolYear());
        if (stored?.jobId) {
          deps.setFreshJobState(false);
          startGenerating(stored.jobId);
        } else if (stored?.idempotencyKey) {
          await submit();
        }
      })();
    },
    detach: () => {
      active = false;
      stopTimer();
    },
    setOpts: (next: GenerationOpts) => {
      opts = next;
    },
  };
}

export function useProgramEvaluationGeneration(opts: GenerationOpts): GenerationResult {
  const [phase, setPhase] = useState<GenerationPhase>('idle');
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobUpdatedAt, setJobUpdatedAt] = useState<string | null>(null);
  const [pollExhausted, setPollExhausted] = useState(false);
  const [errorClass, setErrorClass] = useState<string | null>(null);
  const [freshJob, setFreshJob] = useState(false);

  const [controller] = useState(() =>
    createController(
      {
        setPhaseState: setPhase,
        setJobIdState: setJobId,
        setJobUpdatedAtState: setJobUpdatedAt,
        setPollExhaustedState: setPollExhausted,
        setErrorClassState: setErrorClass,
        setFreshJobState: setFreshJob,
      },
      opts,
    ),
  );

  useEffect(() => {
    controller.setOpts(opts);
  }, [controller, opts]);

  useEffect(() => {
    if (opts.enabled !== false) {
      controller.attach();
    }
  }, [controller, opts.enabled]);

  useEffect(() => () => controller.detach(), [controller]);

  return {
    phase,
    jobId,
    jobUpdatedAt,
    errorClass,
    pollExhausted,
    freshJob,
    submit: controller.submit,
    retry: controller.retry,
    regenerate: controller.regenerate,
  };
}
